import React, { FC, useEffect, useState } from 'react';
import {
    Datagrid,
    FieldProps,
    Filter,
    List,
    Pagination,
    SelectInput,
    TextField,
    TextInput,
    useDataProvider,
    useMutation,
    useNotify,
    useRecordContext,
    useRefresh
} from 'react-admin';
import { getAssigneesFromDb } from "../tickets/AssigneeNNaSelection";
import { Tooltip } from '@material-ui/core';

const Toggle = ({ source }: { source: string }) => {
    const record = useRecordContext();
    const [mutate, { loading }] = useMutation();
    const notify = useNotify();
    const refresh = useRefresh();

    // Handler function that accepts the full record
    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (record) {
            console.debug("Toggle: handleChange: record: " + JSON.stringify(record));

            const updatedRecord = {
                ...record,
                [source]: event.target.checked,
            };

            await mutate({
                type: 'update',
                resource: 'ticketadmin',
                payload: { id: record.id, data: updatedRecord, previousData: record }
            }, {
                onSuccess: ({ data }) => {
                    notify('Changes saved', 'info');
                    refresh();
                },
                onFailure: (error) => {
                    notify(`Error: could not save changes. ${error.message}`, 'warning');
                }
            });
        }
    };

    const isDisabled = record && record.notification === false && (
            source === 'notifyAllChanges' ||
            source === 'onlyAssignedToMe' ||
            source === 'closedTickets' ||
            source === 'newTickets' ||
            source === 'updatedTickets' ||
            source === 'slaBreachNotification'
    );
    const isDisabled2 = record && record.notifyAllChanges === true && (
            source === 'onlyAssignedToMe' ||
            source === 'closedTickets' ||
            source === 'newTickets' ||
            source === 'updatedTickets'
    );

    return record ? (
            <input
                    type="checkbox"
                    checked={record[source]}
                    onChange={handleChange}
                    disabled={loading || isDisabled || isDisabled2}
            />
    ) : null;
};

interface Assignee {
    customerid: string;
    nnacd: string;
}

interface TicketAdminFilterProps {
    defaultAssignees: Assignee[];
}

const TicketAdminFilter: React.FC<React.PropsWithChildren<TicketAdminFilterProps>> = (props) => {
    const { defaultAssignees, children } = props;

    let choices = defaultAssignees.map(assignee => ({
        id: assignee.customerid,
        name: assignee.nnacd
    }));

    // Sort by name
    choices.sort((a, b) => a.name.localeCompare(b.name));

    // Remove duplicates by id
    const uniqueIds = new Set();
    const distinctChoices = choices.filter(choice => {
        if (!uniqueIds.has(choice.id)) {
            uniqueIds.add(choice.id);
            return true;
        }
        return false;
    });

    return (
            <Filter {...props}>
                <TextInput label="Search by user name" source="userName" alwaysOn />
                <SelectInput
                        label="Select customer"
                        source="customer_id"
                        choices={distinctChoices}
                        alwaysOn
                />
            </Filter>
    );
};

export const TicketAdmin: React.FC = (props) => {
    const dataProvider = useDataProvider();
    const [defaultAssignees, setDefaultAssignees] = useState<Assignee[]>([]);

    useEffect(() => {
        const fetchAssignees = async () => {
            try {
                const response = await getAssigneesFromDb(dataProvider);
                setDefaultAssignees(response);
            } catch (error) {
                console.info("error in useeffect dataProvider go data: " + error);
                console.error(error);
            }
        };

        fetchAssignees();
    }, [dataProvider]);

    return (
            <List
                    {...props}
                    bulkActionButtons={false}
                    filters={<TicketAdminFilter defaultAssignees={defaultAssignees} />}
                    perPage={25}
                    actions={false}
                    pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            >
                <Datagrid>
                    <TextField source="userName" />
                    <Toggle source="active" />
                    <Toggle source="notification" />
                    <Toggle source="onlyAssignedToMe" />
                    <Toggle source="closedTickets" />
                    <Toggle source="newTickets" />
                    <Toggle source="updatedTickets" />
                    <Toggle source="slaBreachNotification" />
                    <Toggle source="notifyAllChanges" />
                </Datagrid>
            </List>
    );
};
